import { useState, useEffect, useCallback } from 'react';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, DocumentData } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { 
  Warehouse, 
  CreateWarehouseData,
  WarehouseLocation,
  CreateWarehouseLocationData
} from '../types/warehouse';

export const useWarehouses = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseLocations, setWarehouseLocations] = useState<WarehouseLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const { hasAccess } = usePermissions();

  // Raktárak betöltése
  useEffect(() => {
    const fetchWarehouses = async () => {
      // Ha nincs hozzáférés, ne töltsünk be adatokat
      if (!hasAccess('inventory')) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const warehousesRef = collection(db, 'warehouses');
        const q = query(warehousesRef, where('isActive', '==', true));
        const snapshot = await getDocs(q);
        
        const warehousesList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        })) as Warehouse[];
        
        setWarehouses(warehousesList);
        setError(null);
      } catch (err) {
        console.error('Hiba a raktárak betöltésekor:', err);
        setError('Nem sikerült betölteni a raktárakat.');
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, [hasAccess]);

  // Raktárhelyek betöltése
  const fetchLocationsForWarehouse = useCallback(async (warehouseId: string) => {
    try {
      const locationsRef = collection(db, 'warehouseLocations');
      const q = query(locationsRef, where('warehouseId', '==', warehouseId), where('isActive', '==', true));
      const snapshot = await getDocs(q);
      
      const locationsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) as WarehouseLocation[];
      
      setWarehouseLocations(prevLocations => {
        const filteredLocations = prevLocations.filter(loc => loc.warehouseId !== warehouseId);
        return [...filteredLocations, ...locationsList];
      });
      
      return locationsList;
    } catch (err) {
      console.error(`Hiba a raktárhelyek betöltésekor (${warehouseId}):`, err);
      throw new Error('Nem sikerült betölteni a raktárhelyeket.');
    }
  }, []);

  // Új raktár létrehozása
  const createWarehouse = useCallback(async (data: CreateWarehouseData): Promise<Warehouse> => {
    try {
      const warehouseData: Omit<Warehouse, 'id'> = {
        ...data,
        isActive: data.isActive !== undefined ? data.isActive : true,
        isDefault: data.isDefault !== undefined ? data.isDefault : false,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUser?.uid || 'system'
      };
      
      const docRef = await addDoc(collection(db, 'warehouses'), warehouseData);
      
      const newWarehouse = {
        id: docRef.id,
        ...warehouseData
      } as Warehouse;
      
      setWarehouses(prev => [...prev, newWarehouse]);
      
      return newWarehouse;
    } catch (err) {
      console.error('Hiba a raktár létrehozásakor:', err);
      throw new Error('Nem sikerült létrehozni a raktárt.');
    }
  }, [currentUser]);

  // Raktár frissítése
  const updateWarehouse = useCallback(async (id: string, data: Partial<CreateWarehouseData>): Promise<Warehouse> => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date()
      };
      
      const docRef = doc(db, 'warehouses', id);
      await updateDoc(docRef, updateData);
      
      const updatedWarehouse = warehouses.find(w => w.id === id);
      if (!updatedWarehouse) {
        throw new Error('A raktár nem található.');
      }
      
      const warehouse = {
        ...updatedWarehouse,
        ...updateData
      } as Warehouse;
      
      setWarehouses(prev => 
        prev.map(w => w.id === id ? warehouse : w)
      );
      
      return warehouse;
    } catch (err) {
      console.error(`Hiba a raktár (${id}) frissítésekor:`, err);
      throw new Error('Nem sikerült frissíteni a raktárt.');
    }
  }, [warehouses]);

  // Raktár törlése (inaktiválása)
  const deleteWarehouse = useCallback(async (id: string): Promise<void> => {
    try {
      // Ellenőrizzük, hogy vannak-e készlet rekordok ebben a raktárban
      const inventoryRef = collection(db, 'inventory');
      const inventoryQuery = query(inventoryRef, where('warehouseId', '==', id));
      const inventorySnapshot = await getDocs(inventoryQuery);
      
      if (!inventorySnapshot.empty) {
        // Van készlet a raktárban, nem törölhető
        throw new Error('A raktár nem törölhető, mert tartalmaz készlet rekordokat. Először távolítsa el vagy helyezze át az összes terméket.');
      }
      
      // Ellenőrizzük, hogy vannak-e készletmozgások ehhez a raktárhoz
      const movementsRef = collection(db, 'inventoryMovements');
      const movementsQuery = query(movementsRef, where('warehouseId', '==', id));
      const movementsSnapshot = await getDocs(movementsQuery);
      
      if (!movementsSnapshot.empty) {
        // Vannak készletmozgások, csak inaktiváljuk
        const docRef = doc(db, 'warehouses', id);
        await updateDoc(docRef, {
          isActive: false,
          updatedAt: new Date()
        });
        
        setWarehouses(prev => 
          prev.map(w => w.id === id ? { ...w, isActive: false } : w)
        );
        
        throw new Error('A raktár inaktiválva lett, mert tartalmaz készletmozgás történetet. A raktár nem látható többé, de az adatok megmaradnak.');
      }
      
      // Ha nincs készlet és nincs mozgástörténet, teljesen törölhetjük
      const docRef = doc(db, 'warehouses', id);
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: new Date()
      });
      
      setWarehouses(prev => 
        prev.filter(w => w.id !== id)
      );
    } catch (err) {
      console.error(`Hiba a raktár (${id}) törlésekor:`, err);
      if (err instanceof Error) {
        throw err; // A specifikus hibaüzenetet továbbítjuk
      } else {
        throw new Error('Nem sikerült törölni a raktárt.');
      }
    }
  }, []);

  // Új raktárhely létrehozása
  const createWarehouseLocation = useCallback(async (data: CreateWarehouseLocationData): Promise<WarehouseLocation> => {
    try {
      const locationData: Omit<WarehouseLocation, 'id'> = {
        ...data,
        isActive: data.isActive !== undefined ? data.isActive : true,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      const docRef = await addDoc(collection(db, 'warehouseLocations'), locationData);
      
      const newLocation = {
        id: docRef.id,
        ...locationData
      } as WarehouseLocation;
      
      setWarehouseLocations(prev => [...prev, newLocation]);
      
      return newLocation;
    } catch (err) {
      console.error('Hiba a raktárhely létrehozásakor:', err);
      throw new Error('Nem sikerült létrehozni a raktárhelyet.');
    }
  }, []);

  // Raktárhely frissítése
  const updateWarehouseLocation = useCallback(async (id: string, data: Partial<CreateWarehouseLocationData>): Promise<WarehouseLocation> => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date()
      };
      
      const docRef = doc(db, 'warehouseLocations', id);
      await updateDoc(docRef, updateData);
      
      const updatedLocation = warehouseLocations.find(l => l.id === id);
      if (!updatedLocation) {
        throw new Error('A raktárhely nem található.');
      }
      
      const location = {
        ...updatedLocation,
        ...updateData
      } as WarehouseLocation;
      
      setWarehouseLocations(prev => 
        prev.map(l => l.id === id ? location : l)
      );
      
      return location;
    } catch (err) {
      console.error(`Hiba a raktárhely (${id}) frissítésekor:`, err);
      throw new Error('Nem sikerült frissíteni a raktárhelyet.');
    }
  }, [warehouseLocations]);

  // Raktárhely törlése (inaktiválása)
  const deleteWarehouseLocation = useCallback(async (id: string): Promise<void> => {
    try {
      const docRef = doc(db, 'warehouseLocations', id);
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: new Date()
      });
      
      setWarehouseLocations(prev => 
        prev.filter(l => l.id !== id)
      );
    } catch (err) {
      console.error(`Hiba a raktárhely (${id}) törlésekor:`, err);
      throw new Error('Nem sikerült törölni a raktárhelyet.');
    }
  }, []);

  return {
    warehouses,
    warehouseLocations,
    loading,
    error,
    fetchLocationsForWarehouse,
    createWarehouse,
    updateWarehouse,
    deleteWarehouse,
    createWarehouseLocation,
    updateWarehouseLocation,
    deleteWarehouseLocation
  };
}; 